import {ObservationCard} from "./observation-card";
import {ReactNode, useContext, useEffect, useState} from "react";
import {DateTime} from 'luxon';
import {AppContext} from "../App";
import {EBirdTaxonomyEntry} from "../helpers/useEBirdTaxonomy";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendarDays, faClock, faHashtag, faLocationDot} from "@fortawesome/free-solid-svg-icons";
import type {SpeciesFamilyLink} from "../App";
import {getMacaulayCatalogNumbers} from "../helpers/macaulayLibrary";

export type InfoboxObservationCardProps = {
    currentMarker: any,
    currentIndex: number,
    total: number,
    year?: string|number,
    month?: string|number,
    familyLink?: SpeciesFamilyLink
}

export function InfoboxSingleObservation({currentMarker, currentIndex, total, year, month, familyLink}: InfoboxObservationCardProps) {
    const [intro, setIntro] = useState<ReactNode>();
    const [title, setTitle] = useState<ReactNode>();
    const [image, setImage] = useState<string>();
    const [kicker, setKicker] = useState<string>();
    const [copy, setCopy] = useState<ReactNode>();
    const [sourceUrl, setSourceUrl] = useState<string>();
    const [badges, setBadges] = useState<string[]>([]);
    const [macaulayCatalogNumbers, setMacaulayCatalogNumbers] = useState<string[]>([]);

    const appContext = useContext(AppContext);

    useEffect(() => {
        if (!currentMarker?.properties) return;
        const props = currentMarker.properties;

        const taxonomyResult: EBirdTaxonomyEntry|undefined = appContext.lookupByTaxonomicOrder(props.taxonomicOrder);

        setTitle(props.species);
        setSourceUrl(taxonomyResult ? `https://ebird.org/species/${taxonomyResult.speciesCode}` : undefined);

        setImage(props.image);
        setMacaulayCatalogNumbers(getMacaulayCatalogNumbers(props.mlCatalogNumbers));

        const date = DateTime.fromISO(props.date);
        const dateLong = date.toFormat('cccc, LLLL d, y');
        const parsedTime = props.time ? DateTime.fromFormat(props.time, 'HH:mm') : undefined;
        const time = parsedTime?.isValid ? parsedTime.toLocaleString(DateTime.TIME_SIMPLE) : props.time;
        setCopy(
            <>
                <span className="observation-card__meta-line">
                    <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                    <span>{dateLong}</span>
                </span>
                {time ?
                    <span className="observation-card__meta-line">
                        <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                        <span>{time}</span>
                    </span> :
                    null
                }
                <span className="observation-card__meta-line">
                    <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                    <span>{[props.locationName, props.locationName2].filter(Boolean).join(' - ')}</span>
                </span>
                <span className="observation-card__meta-line observation-card__meta-line--count">
                    <FontAwesomeIcon icon={faHashtag} aria-hidden="true" />
                    <span>Count: {props.count}</span>
                </span>
            </>
        )

        if (currentIndex + 1 && total) {
            setKicker(`${currentIndex + 1}/${total}`);
        }

        const observationBadges = [];
        if (props.isLifer) observationBadges.push('Lifer');
        else if (props.isFirstOfYear) observationBadges.push('First of Year');
        setIntro(undefined);
        setBadges(observationBadges);
    }, [currentMarker, total, currentIndex, appContext]);

    return (
        <>
            {intro ?
                <h2 className="infobox__title">{intro}</h2>
                : null
            }
            
            <ObservationCard
                image={image}
                kicker={kicker}
                title={title}
                copy={copy}
                badges={badges}
                familyLink={familyLink ? {
                    label: familyLink.familyName,
                    to: `/checklist/family/${familyLink.familySlug}`
                } : undefined}
                sourceUrl={sourceUrl}
                macaulayCatalogNumbers={macaulayCatalogNumbers}
            />
        </>
    )
}
